import agent, { ListData } from "../api/agent";
import { Member, MemberFormValues } from "../models/member";
import { DataTableList } from "../common/interfaces/TableData";
import { FilterProps } from "../common/DataTable";
import { NewMember } from "../common/interfaces/NewMember";
import { toast } from "react-toastify";
import { MembershipPaymentByYearDto } from "../models/MembershipPaymentByYearDto";

export type MemberStore = {
  tableData: DataTableList<Member>;
  selectedMember?: Member;
  loadingInitial: boolean;
  editMode: boolean;
  loading: boolean;
  membershipPaymentsByYear: DataTableList<MembershipPaymentByYearDto>;

  loadMembers: (filterProps: FilterProps) => Promise<ListData<Member>>;
  loadUnpaidMemberships: (filterProps: FilterProps) => Promise<ListData<Member> | void>;
  loadPaidMemberships: (filterProps: FilterProps) => Promise<ListData<Member> | void>;
  loadMember: (id: string) => Promise<Member | undefined>;
  setLoadingInitial: (state: boolean) => void;
  createMember: (member: MemberFormValues) => Promise<void>;
  updateMember: (member: MemberFormValues) => Promise<void>;
  deleteMember: (id: string) => Promise<void>;
  approveMember: (id: string) => Promise<void>;
  uploadMembersXlsxData: (xlsxData: FormData) => Promise<void>;
  clearSelectedMember: () => void;
  registerMember: (member: NewMember) => Promise<boolean>;
  loadPendingMembersTable: (filterProps: FilterProps) => Promise<ListData<Member>>;
  loadMembershipPaymentsByYear: (id: string) => Promise<void>;
};

export const createMemberStore = (set: any, get: any): MemberStore => ({
  tableData: new DataTableList<Member>(),
  selectedMember: undefined,
  loadingInitial: false,
  editMode: false,
  loading: false,
  membershipPaymentsByYear: new DataTableList<MembershipPaymentByYearDto>(),

  loadMembers: async (filterProps: FilterProps) => {
    set((s: any) => ({ memberStore: { ...s.memberStore, loadingInitial: true } }));
    try {
      const result = await agent.Members.list(filterProps);
      const list = result.data.reduce((acc: Map<string, Member>, item: Member) => {
        acc.set(item.id!, item);
        return acc;
      }, new Map<string, Member>());
      set((s: any) => ({
        memberStore: {
          ...s.memberStore,
          tableData: {
            list,
            currentPage: result.currentPage,
            pageSize: result.pageSize,
            totalRecords: result.totalRecords,
            orderBy: result.orderBy,
          },
          loadingInitial: false,
        },
      }));
      return result;
    } catch (error) {
      console.error(error);
      set((s: any) => ({ memberStore: { ...s.memberStore, loadingInitial: false } }));
      throw error;
    }
  },

  loadUnpaidMemberships: async (filterProps: FilterProps) => {
    set((s: any) => ({ memberStore: { ...s.memberStore, loadingInitial: true } }));
    try {
      const result = await agent.Members.getUnpaidMemberships(filterProps);
      const list = result.data.reduce((acc: Map<string, Member>, item: Member) => {
        acc.set(item.id!, item);
        return acc;
      }, new Map<string, Member>());
      set((s: any) => ({
        memberStore: {
          ...s.memberStore,
          tableData: {
            list,
            currentPage: result.currentPage,
            pageSize: result.pageSize,
            totalRecords: result.totalRecords,
            orderBy: result.orderBy,
          },
          loadingInitial: false,
        },
      }));
      return result;
    } catch (error) {
      console.log(error);
      set((s: any) => ({ memberStore: { ...s.memberStore, loadingInitial: false } }));
    }
  },

  loadPaidMemberships: async (filterProps: FilterProps) => {
    set((s: any) => ({ memberStore: { ...s.memberStore, loadingInitial: true } }));
    try {
      const result = await agent.Members.getPaidMemberships(filterProps);
      const list = result.data.reduce((acc: Map<string, Member>, item: Member) => {
        acc.set(item.id!, item);
        return acc;
      }, new Map<string, Member>());
      set((s: any) => ({
        memberStore: {
          ...s.memberStore,
          tableData: {
            list,
            currentPage: result.currentPage,
            pageSize: result.pageSize,
            totalRecords: result.totalRecords,
            orderBy: result.orderBy,
          },
          loadingInitial: false,
        },
      }));
      return result;
    } catch (error) {
      console.log(error);
      set((s: any) => ({ memberStore: { ...s.memberStore, loadingInitial: false } }));
    }
  },

  loadMember: async (id: string) => {
    const existing = get().memberStore.tableData.list.get(id);
    if (existing) {
      set((s: any) => ({ memberStore: { ...s.memberStore, selectedMember: existing } }));
      return existing;
    }
    set((s: any) => ({ memberStore: { ...s.memberStore, loadingInitial: true } }));
    try {
      const member = await agent.Members.details(id);
      const list = new Map(get().memberStore.tableData.list);
      if (member?.id) list.set(member.id, member);
      set((s: any) => ({
        memberStore: {
          ...s.memberStore,
          tableData: { ...s.memberStore.tableData, list },
          selectedMember: member,
          loadingInitial: false,
        },
      }));
      return member;
    } catch (error) {
      console.log(error);
      set((s: any) => ({ memberStore: { ...s.memberStore, loadingInitial: false } }));
    }
  },

  setLoadingInitial: (state: boolean) => {
    set((s: any) => ({ memberStore: { ...s.memberStore, loadingInitial: state } }));
  },

  createMember: async (member: MemberFormValues) => {
    try {
      await agent.Members.create(member);
      const newMember = new Member(member);
      const list = new Map(get().memberStore.tableData.list);
      if (newMember.id) list.set(newMember.id, newMember);
      set((s: any) => ({
        memberStore: {
          ...s.memberStore,
          tableData: { ...s.memberStore.tableData, list },
          selectedMember: newMember,
        },
      }));
    } catch (error) {
      console.log(error);
      return Promise.reject(error);
    }
  },

  updateMember: async (member: MemberFormValues) => {
    try {
      await agent.Members.update(member);
      const list = new Map(get().memberStore.tableData.list);
      if (member.id) {
        const existing = list.get(member.id);
        list.set(
          member.id,
          {
            ...(existing || {}),
            ...member,
            dateOfBirth: member.dateOfBirth ? new Date(member.dateOfBirth) : undefined,
          } as Member
        );
      }
      set((s: any) => ({
        memberStore: {
          ...s.memberStore,
          tableData: { ...s.memberStore.tableData, list },
          selectedMember: member.id ? (list.get(member.id) as Member) : s.memberStore.selectedMember,
        },
      }));
    } catch (error) {
      console.log(error);
      return Promise.reject(error);
    }
  },

  deleteMember: async (id: string) => {
    set((s: any) => ({ memberStore: { ...s.memberStore, loading: true } }));
    try {
      await agent.Members.delete(id);
      const list = new Map(get().memberStore.tableData.list);
      list.delete(id);
      set((s: any) => ({
        memberStore: {
          ...s.memberStore,
          tableData: { ...s.memberStore.tableData, list },
          loading: false,
        },
      }));
    } catch (error) {
      console.log(error);
      set((s: any) => ({ memberStore: { ...s.memberStore, loading: false } }));
      return Promise.reject(error);
    }
  },

  approveMember: async (id: string) => {
    set((s: any) => ({ memberStore: { ...s.memberStore, loading: true } }));
    try {
      await agent.Members.approveMember(id);
      const list = new Map(get().memberStore.tableData.list);
      list.delete(id);
      set((s: any) => ({
        memberStore: {
          ...s.memberStore,
          tableData: { ...s.memberStore.tableData, list },
          loading: false,
        },
      }));
    } catch (error) {
      console.log(error);
      set((s: any) => ({ memberStore: { ...s.memberStore, loading: false } }));
      return Promise.reject(error);
    }
  },

  uploadMembersXlsxData: async (xlsxData: FormData) => {
    set((s: any) => ({ memberStore: { ...s.memberStore, loading: true } }));
    try {
      await agent.Members.uploadMembers(xlsxData);
    } catch (error) {
      console.log(error);
      return Promise.reject(error);
    } finally {
      set((s: any) => ({ memberStore: { ...s.memberStore, loading: false } }));
    }
  },

  clearSelectedMember: () => {
    set((s: any) => ({ memberStore: { ...s.memberStore, selectedMember: undefined } }));
  },

  registerMember: async (member: NewMember) => {
    try {
      await agent.Members.register(member);
      toast.success("Erfolgreich ausgeführt!");
      return true;
    } catch (error) {
      console.log(error);
      return Promise.reject(error);
    }
  },

  loadPendingMembersTable: async (filterProps: FilterProps) => {
    set((s: any) => ({ memberStore: { ...s.memberStore, loadingInitial: true } }));
    try {
      const result = await agent.Members.getPendingMembers(filterProps);
      const list = result.data.reduce((acc: Map<string, Member>, item: Member) => {
        acc.set(item.id!, item);
        return acc;
      }, new Map<string, Member>());
      set((s: any) => ({
        memberStore: {
          ...s.memberStore,
          tableData: {
            list,
            currentPage: result.currentPage,
            pageSize: result.pageSize,
            totalRecords: result.totalRecords,
            orderBy: result.orderBy,
          },
          loadingInitial: false,
        },
      }));
      return result;
    } catch (error) {
      console.error(error);
      set((s: any) => ({ memberStore: { ...s.memberStore, loadingInitial: false } }));
      throw error;
    }
  },

  loadMembershipPaymentsByYear: async (id: string) => {
    try {
      const result = await agent.Members.getMembershipPaymentsByYearAndMember(id);
      const list = result.data.reduce((acc: Map<string, MembershipPaymentByYearDto>, item: any) => {
        acc.set(item.year.toString(), item);
        return acc;
      }, new Map<string, MembershipPaymentByYearDto>());
      set((s: any) => ({
        memberStore: {
          ...s.memberStore,
          membershipPaymentsByYear: {
            list,
            totalRecords: result.totalRecords,
            currentPage: result.currentPage,
            pageSize: result.pageSize,
            orderBy: result.orderBy,
          },
        },
      }));
    } catch (error) {
      console.error("Failed to load membership payments by year", error);
    }
  },
});